import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement,
  RadialLinearScale, PointElement, LineElement,
  Filler, Tooltip, Legend,
  ChartData, ChartOptions
} from 'chart.js';
import { Bar, Radar } from 'react-chartjs-2';
import { GlassCard } from '@/components/common/GlassCard';
import './ExecutiveView.css';
import {
  useAnalyticsSummary,
  useAnalyticsFundamentals,
  useAnalyticsHeatmap,
} from '@/hooks/useAnalytics';
import { useOUItems, useOUs } from '@/hooks/useOUs';
import { USE_MOCK, type AnalyticsFilterParams } from '@/api/client';
import { buildOuTree, type OuNode } from '@/components/common/OuTree/ouTree';
import { fmtScore } from '@/utils/analytics';
import {
  SIN_OU_KEY,
  aggregateHeatmapByOu,
  averageForFundamental,
  mergeAggregateForNode,
} from '@/utils/heatmapAggregation';
import { buildExecutivePeriods, ALL_HISTORY_PERIOD, type ExecutivePeriod } from './periods';

ChartJS.register(CategoryScale, LinearScale, BarElement, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

type AnalyticsTab = 'summary' | 'fundamentals' | 'heatmap' | 'ona' | 'generational' | 'bias' | 'omissions';

const TABS: Array<{ id: AnalyticsTab; icon: string; labelKey: string }> = [
  { id: 'summary',      icon: 'fa-chart-pie',      labelKey: 'analytics.tabs.summary'      },
  { id: 'fundamentals', icon: 'fa-layer-group',    labelKey: 'analytics.tabs.fundamentals' },
  { id: 'heatmap',      icon: 'fa-table-cells',    labelKey: 'analytics.tabs.heatmap'      },
  { id: 'ona',          icon: 'fa-share-nodes',    labelKey: 'analytics.ona.title'         },
  { id: 'generational', icon: 'fa-people-group',   labelKey: 'analytics.tabs.generational' },
  { id: 'bias',         icon: 'fa-scale-balanced', labelKey: 'analytics.bias'              },
  { id: 'omissions',    icon: 'fa-eye-slash',      labelKey: 'analytics.omissions'         },
];

/** Rangos del mapa de calor: cada tramo con su color y la etiqueta que se muestra en la leyenda. */
const HEAT_BANDS = [
  { minScore: 3.5, color: 'rgba(32,177,90,0.7)',  label: '3.5–4.0' },
  { minScore: 2.5, color: 'rgba(64,184,233,0.6)', label: '2.5–3.4' },
  { minScore: 1.5, color: 'rgba(240,133,33,0.6)', label: '1.5–2.4' },
  { minScore: 0,   color: 'rgba(225,31,38,0.6)',  label: '1.0–1.4' },
];

const BAR_COLORS = [
  'rgba(52,47,130,0.7)', 'rgba(64,184,233,0.7)', HEAT_BANDS[0]!.color,
  'rgba(240,133,33,0.7)', 'rgba(239,83,149,0.7)', 'rgba(225,31,38,0.7)',
];

/** Único mensaje de "sin datos" de la vista — se muestra en los tres tabs conectados. */
const NO_DATA_KEY = 'analytics.noData';

/** Fila del mapa de calor ejecutivo: una OU raíz con su promedio por fundamental. */
interface ExecutiveHeatmapRow {
  id: string;
  name: string;
  /** Alineado con las etiquetas de fundamentales; `null` = sin evaluaciones. */
  scores: Array<number | null>;
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="exec-empty">
      <i className="fas fa-chart-simple" aria-hidden="true" />
      <p>{message}</p>
    </div>
  );
}

interface SummaryTabProps {
  avgScore?: number;
  responses?: number;
  participants?: number;
  completionRate?: number;
  fundLabels: string[];
  fundScores: number[];
}

function SummaryTab({ avgScore, responses, participants, completionRate, fundLabels, fundScores }: SummaryTabProps) {
  const { t } = useTranslation();

  const barData: ChartData<'bar'> = {
    labels: fundLabels,
    datasets: [{
      label: t('analytics.score'),
      data: fundScores,
      backgroundColor: BAR_COLORS,
      borderRadius: 6,
    }],
  };

  const barOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: { min: 0, max: 4, ticks: { stepSize: 1 } },
    },
  };

  const kpis = [
    { label: t('analytics.score'),          value: fmtScore(avgScore),                              icon: 'fa-star',      color: 'var(--cp-purple)' },
    { label: t('analytics.responses'),      value: responses !== undefined ? responses : '—',       icon: 'fa-clipboard', color: 'var(--cp-cyan)' },
    { label: t('analytics.participants'),   value: participants !== undefined ? participants : '—', icon: 'fa-users',     color: 'var(--cp-green)' },
    { label: t('analytics.completionRate'), value: completionRate !== undefined ? `${completionRate}%` : '—', icon: 'fa-percent', color: 'var(--cp-orange)' },
  ];

  return (
    <div className="exec-tab-content">
      {/* KPI strip */}
      <div className="exec-kpi-strip">
        {kpis.map((kpi) => (
          <GlassCard key={kpi.icon} className="exec-kpi-card">
            <div className="exec-kpi-icon" style={{ color: kpi.color, background: `${kpi.color}18` }}>
              <i className={`fas ${kpi.icon}`} />
            </div>
            <div className="exec-kpi-body">
              <span className="exec-kpi-value">{kpi.value}</span>
              <span className="exec-kpi-label">{kpi.label}</span>
            </div>
          </GlassCard>
        ))}
      </div>

      {/* Bar chart */}
      <GlassCard className="exec-chart-card">
        <div className="exec-chart-title">
          <i className="fas fa-bar-chart" />
          {t('analytics.tabs.fundamentals')} — {t('analytics.avgByCategory')}
        </div>
        {fundLabels.length > 0 ? (
          <div className="exec-chart-body">
            <Bar data={barData} options={barOptions} />
          </div>
        ) : (
          <EmptyState message={t(NO_DATA_KEY)} />
        )}
      </GlassCard>
    </div>
  );
}

interface FundamentalsTabProps {
  fundLabels: string[];
  fundScores: number[];
}

function FundamentalsTab({ fundLabels, fundScores }: FundamentalsTabProps) {
  const { t } = useTranslation();

  const radarData: ChartData<'radar'> = {
    labels: fundLabels,
    datasets: [{
      label: t('analytics.organization'),
      data: fundScores,
      borderColor: '#342F82',
      backgroundColor: 'rgba(52,47,130,0.18)',
      pointBackgroundColor: '#342F82',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
    }],
  };

  const radarOptions: ChartOptions<'radar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom', labels: { usePointStyle: true, padding: 20 } } },
    scales: {
      r: { min: 0, max: 4, ticks: { stepSize: 1, display: false }, pointLabels: { font: { size: 11 } } },
    },
  };

  if (fundLabels.length === 0) {
    return (
      <div className="exec-tab-content">
        <GlassCard className="exec-chart-card">
          <EmptyState message={t(NO_DATA_KEY)} />
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="exec-tab-content">
      <div className="exec-charts-row">
        <GlassCard className="exec-chart-card">
          <div className="exec-chart-title"><i className="fas fa-star" /> {t('analytics.fundamentalsRadar')}</div>
          <div className="exec-chart-body"><Radar data={radarData} options={radarOptions} /></div>
        </GlassCard>

        <div className="exec-fund-list">
          {fundLabels.map((label, index) => {
            const score = fundScores[index] ?? 0;
            return (
              <GlassCard key={label} className="exec-fund-row">
                <span className="exec-fund-name">{label}</span>
                <div className="exec-fund-bar-bg">
                  <div className="exec-fund-bar-fill" style={{ width: `${(score / 4) * 100}%` }} />
                </div>
                <span className="exec-fund-score">{score.toFixed(1)}</span>
              </GlassCard>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** Color de celda del mapa de calor; `null` (sin evaluaciones) usa un gris neutro, no el rojo de score bajo. */
function heatColor(score: number | null): string {
  if (score === null) return 'var(--exec-heat-nodata)';
  const band = HEAT_BANDS.find((candidate) => score >= candidate.minScore);
  return band?.color ?? 'var(--exec-heat-nodata)';
}

interface HeatmapTabProps {
  rows: ExecutiveHeatmapRow[];
  fundLabels: string[];
}

function HeatmapTab({ rows, fundLabels }: HeatmapTabProps) {
  const { t } = useTranslation();

  return (
    <div className="exec-tab-content">
      <GlassCard className="exec-heatmap-card">
        <div className="exec-chart-title"><i className="fas fa-table-cells" /> {t('analytics.heatmapOuFund')}</div>
        {rows.length > 0 && fundLabels.length > 0 ? (
          <>
            <div className="exec-heatmap-wrap">
              <table className="exec-heatmap-table">
                <thead>
                  <tr>
                    <th>{t('analytics.heatmap.unitOU')}</th>
                    {fundLabels.map((label) => <th key={label}>{label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td className="exec-heatmap-person">{row.name}</td>
                      {fundLabels.map((label, index) => {
                        const score = row.scores[index] ?? null;
                        return (
                          <td
                            key={label}
                            className={`exec-heatmap-cell${score === null ? ' is-empty' : ''}`}
                            style={{ background: heatColor(score) }}
                          >
                            {fmtScore(score ?? undefined)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="exec-heatmap-legend">
              {[...HEAT_BANDS].reverse().map((band) => (
                <span key={band.label} style={{ background: band.color }}>{band.label}</span>
              ))}
            </div>
          </>
        ) : (
          <EmptyState message={t(NO_DATA_KEY)} />
        )}
      </GlassCard>
    </div>
  );
}

function PlaceholderTab({ label }: { label: string }) {
  const { t } = useTranslation();
  return (
    <div className="exec-tab-content exec-placeholder">
      <i className="fas fa-chart-network" />
      <p>{label} — {t('analytics.moduleInDev')}</p>
    </div>
  );
}

export function ExecutiveView() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<AnalyticsTab>('summary');
  const [periodId, setPeriodId] = useState(ALL_HISTORY_PERIOD);
  const [ouId, setOuId] = useState('');

  const periods = useMemo<ExecutivePeriod[]>(
    () => buildExecutivePeriods(new Date(), i18n.language),
    [i18n.language],
  );
  const selectedPeriod = periods.find((period) => period.id === periodId);

  const apiFilters = useMemo<AnalyticsFilterParams>(() => ({
    ouId: ouId || undefined,
    startDate: selectedPeriod?.dateFrom,
    endDate: selectedPeriod?.dateTo,
    language: i18n.language.split('-')[0],
  }), [ouId, selectedPeriod, i18n.language]);

  const { data: summaryData }      = useAnalyticsSummary(apiFilters);
  const { data: fundamentalsData } = useAnalyticsFundamentals(apiFilters);
  const { data: heatmapData }      = useAnalyticsHeatmap(apiFilters);
  const { data: ouOptions = [] }   = useOUs();
  const { data: ouItems = [] }     = useOUItems({ activeOnly: false });

  // En modo mock los datos quedan undefined y cada tab muestra su estado vacío explícito, sin
  // fallback a constantes ficticias (ver MOCK_FALLBACK.md).
  const apiSummary      = USE_MOCK ? undefined : summaryData;
  const apiFundamentals = USE_MOCK ? undefined : fundamentalsData;
  const apiHeatmap      = USE_MOCK ? undefined : heatmapData;

  const fundLabels = useMemo(() => (apiFundamentals ?? []).map((fund) => fund.name), [apiFundamentals]);
  const fundScores = useMemo(() => (apiFundamentals ?? []).map((fund) => fund.score), [apiFundamentals]);

  const ouForest = useMemo<OuNode[]>(() => buildOuTree(ouItems), [ouItems]);

  // Mapa de calor ejecutivo: una fila por OU raíz, agregando recursivamente a todos sus
  // descendientes reales. Se omiten las OUs sin participantes evaluados para que la tabla no
  // crezca con filas vacías. "Sin OU" aparece solo si el backend devolvió participantes sin OU.
  const heatmapRows = useMemo<ExecutiveHeatmapRow[]>(() => {
    const fundamentalCount = apiHeatmap?.columns.length ?? 0;
    if (fundamentalCount === 0) return [];

    const ouAggregates = aggregateHeatmapByOu(apiHeatmap);
    const rows: ExecutiveHeatmapRow[] = [];

    for (const node of ouForest) {
      const aggregate = mergeAggregateForNode(node, ouAggregates, fundamentalCount);
      if (aggregate.participantCount === 0) continue;
      rows.push({
        id: node.ou.id,
        name: node.ou.name,
        scores: Array.from({ length: fundamentalCount }, (_unused, index) =>
          averageForFundamental(aggregate, index)),
      });
    }

    const withoutOu = ouAggregates.get(SIN_OU_KEY);
    if (withoutOu) {
      rows.push({
        id: SIN_OU_KEY,
        name: t('analytics.heatmap.noOu'),
        scores: Array.from({ length: fundamentalCount }, (_unused, index) =>
          averageForFundamental(withoutOu, index)),
      });
    }

    return rows;
  }, [apiHeatmap, ouForest, t]);

  // El heatmap y el catálogo de fundamentales son dos consultas distintas: se usan los nombres
  // del propio heatmap para que columnas y celdas no se desalineen si difieren.
  const heatmapLabels = (apiHeatmap?.columns ?? []).map((column) => column.name);

  return (
    <div className="executive-view">
      {/* Header */}
      <header className="page-header page-header--center exec-header glass">
        <div>
          <h1 className="page-title">{t('analytics.title')}</h1>
          <p className="page-subtitle">{t('analytics.subtitle')} — {t('analytics.executiveSubtitle')}</p>
        </div>
        <div className="exec-filters">
          <select
            className="exec-select"
            value={periodId}
            onChange={(event) => setPeriodId(event.target.value)}
            aria-label={t('analytics.period')}
          >
            {periods.map((period) => (
              <option key={period.id} value={period.id}>
                {period.id === ALL_HISTORY_PERIOD ? t('analytics.exec.periodAll') : period.label}
              </option>
            ))}
          </select>
          <select
            className="exec-select"
            value={ouId}
            onChange={(event) => setOuId(event.target.value)}
            aria-label={t('analytics.ou')}
          >
            <option value="">{t('analytics.filterBar.allOrg')}</option>
            {ouOptions.map((option) => (
              <option key={option.key} value={option.key}>{option.label}</option>
            ))}
          </select>
        </div>
      </header>

      {/* Tab bar */}
      <div className="exec-tab-bar glass">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`exec-tab-btn${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <i className={`fas ${tab.icon}`} aria-hidden="true" />
            <span>{t(tab.labelKey)}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="exec-content">
        {activeTab === 'summary'      && (
          <SummaryTab
            avgScore={apiSummary?.overallAverage}
            responses={apiSummary?.totalEvaluations}
            participants={apiSummary?.uniqueEvaluators}
            completionRate={apiSummary?.responseRate}
            fundLabels={fundLabels}
            fundScores={fundScores}
          />
        )}
        {activeTab === 'fundamentals' && (
          <FundamentalsTab fundLabels={fundLabels} fundScores={fundScores} />
        )}
        {activeTab === 'heatmap'      && (
          <HeatmapTab rows={heatmapRows} fundLabels={heatmapLabels} />
        )}
        {activeTab === 'ona'          && <PlaceholderTab label={t('analytics.ona.title')} />}
        {activeTab === 'generational' && <PlaceholderTab label={t('analytics.tabs.generational')} />}
        {activeTab === 'bias'         && <PlaceholderTab label={t('analytics.bias')} />}
        {activeTab === 'omissions'    && <PlaceholderTab label={t('analytics.omissions')} />}
      </div>
    </div>
  );
}
